import { GAME_STATES } from '../constants';

const initialState = {
    isAuthLoading: true, // we assume we are loading on reauth
    userRole: 'user',

    game: {
        // default setup so we have something to load in while pending
        gameFen: "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1", // starting position
        isWhite: true,
        gameState: GAME_STATES.inactive,
        placingBombsSeconds: 100, // amount of time alloted to planting bombs - shouldn't change more than once (at set)
        bombTimerSyncedAt: Date.now(),
        moveHistory: [],
        // loggedIn: false,          // playing as a guest also counts as logging in!
        // playingAsGuest: false,
        player: {
            name: "My Name",
            rating: 0,
            bombs: [],
            secondsLeft: 100,
            lastSyncAt: Date.now(),
        },
        opponent: {
            name: "Opponent's Name",
            rating: 0,
            bombs: [],
            secondsLeft: 100,
            lastSyncAt: Date.now(),
            is_guest: false,
        }
    }
}

/*
Rules of Reducers (https://redux.js.org/tutorials/fundamentals/part-3-state-actions-reducers#rules-of-reducers)
- should only calculate new state value based on state and action
- not allowed to modify the existing state. must make immutable updates by making changes to COPIED state
- must not do async logic
*/
export default function appReducer(state = initialState, action) {
    switch (action.type) {
        case "UPDATE_GAME": {
            const { gameFen, moveSan } = action.payload;
            return {
                ...state,
                game: {
                    ...state.game,
                    gameFen,
                    moveHistory: moveSan != null ? [...state.game.moveHistory, moveSan] : state.game.moveHistory,
                }
            };
        }

        case "PLACE_BOMB": {
            const square = action.payload;
            const isMyBomb = (state.game.isWhite && (square[1] === '3' || square[1] === '4')) || (!state.game.isWhite && (square[1] === '5' || square[1] === '6'));
            return {
                ...state,
                game: {
                    ...state.game,
                    player: isMyBomb
                        ? {
                            ...state.game.player,
                            bombs: [...state.game.player.bombs, action.payload]
                        }
                        : state.game.player,
                    opponent: !isMyBomb
                        ? {
                            ...state.game.opponent,
                            bombs: [...state.game.opponent.bombs, action.payload]
                        }
                        : state.game.opponent
                }
            };
        }

        case "DETONATE_BOMB": {
            const squareToExplode = action.payload;
            const isMyBomb = (state.game.isWhite && (squareToExplode[1] === '3' || squareToExplode[1] === '4')) || (!state.game.isWhite && (squareToExplode[1] === '5' || squareToExplode[1] === '6'));
            return {
                ...state,
                game: {
                    ...state.game,
                    player: isMyBomb
                        ? {
                            ...state.game.player,
                            bombs: state.game.player.bombs.filter(sq => sq !== squareToExplode),
                        }
                        : state.game.player,
                    opponent: !isMyBomb
                        ? {
                            ...state.game.opponent,
                            bombs: state.game.opponent.bombs.filter(sq => sq !== squareToExplode),
                        }
                        : state.game.opponent
                }
            };
        }

        case "SET_GAME_FEN":
            return {
                ...state,
                game: {
                    ...state.game,
                    gameFen: action.payload,
                }
            }

        case "SET_GAME_STATE":
            return {
                ...state,
                game: {
                    ...state.game,
                    gameState: action.payload,
                }
            }

        case "SET_PLAYER_INFO":
            return {
                ...state,
                game: {
                    ...state.game,
                    player: action.payload,
                }
            };

        case "SET_OPPONENT_INFO":
            return {
                ...state,
                game: {
                    ...state.game,
                    opponent: action.payload,
                }
            };

        case "SET_ORIENTATION":
            return {
                ...state,
                game: {
                    ...state.game,
                    isWhite: action.payload,
                }
            };

        case "SET_PLACING_BOMBS_SECONDS":
            return {
                ...state,
                game: {
                    ...state.game,
                    placingBombsSeconds: action.payload.secs,
                    bombTimerSyncedAt: action.payload.syncedAt,
                }
            };

        case "SET_MY_BOMBS":
            return {
                ...state,
                game: {
                    ...state.game,
                    player: {
                        ...state.game.player,
                        bombs: action.payload,
                    }
                }
            };

        case "SET_RANDOMIZED_BOMBS": {
            const { whitePlayerBombs, blackPlayerBombs } = action.payload;
            return {
                ...state,
                game: {
                    ...state.game,
                    player: {
                        ...state.game.player,
                        bombs: state.game.isWhite ? whitePlayerBombs : blackPlayerBombs,
                    },
                    // opponent.bombs intentionally NOT set — never expose during active game
                }
            };
        }

        case "SET_TIMERS": {
            const { whiteTimeLeft, blackTimeLeft, syncedAt } = action.payload;
            return {
                ...state,
                game: {
                    ...state.game,
                    player: {
                        ...state.game.player,
                        secondsLeft: state.game.isWhite ? whiteTimeLeft : blackTimeLeft,
                        lastSyncAt: syncedAt,
                    },
                    opponent: {
                        ...state.game.opponent,
                        secondsLeft: state.game.isWhite ? blackTimeLeft : whiteTimeLeft,
                        lastSyncAt: syncedAt,
                    }
                }
            };
        }

        case "LOG_IN":
            return {
                ...state,
                username: action.payload,
                loggedIn: true,
                playingAsGuest: false,
                game: {
                    ...state.game,
                    player: {
                        ...state.game.player,
                        name: action.payload,
                    },
                }
            }

        case "SET_USER_ROLE":
            return { ...state, userRole: action.payload };

        case "LOG_OUT":
            return {
                ...state,
                username: "",
                loggedIn: false,
                playingAsGuest: false,
                userRole: 'user',
                game: {
                    ...state.game,
                    player: initialState.game.player,
                }
            }

        case "PLAY_AS_GUEST":
            return {
                ...state,
                username: action.payload,
                loggedIn: true,
                playingAsGuest: true,
                game: {
                    ...state.game,
                    player: {
                        ...state.game.player,
                        name: action.payload, // unique uuid assigned by server
                        rating: state.game.player.rating || 1500, // default guest ELO; preserve if already updated this session
                    },
                }
            }

        case "UPDATE_PLAYER_RATING":
            return {
                ...state,
                game: {
                    ...state.game,
                    player: {
                        ...state.game.player,
                        rating: action.payload,
                    }
                }
            };

        case "UPDATE_OPPONENT_BOMBS":
            return {
                ...state,
                game: {
                    ...state.game,
                    opponent: {
                        ...state.game.opponent,
                        bombs: action.payload,
                    }
                }
            };

        case "SET_MOVE_HISTORY":
            return {
                ...state,
                game: {
                    ...state.game,
                    moveHistory: action.payload,
                }
            };

        case "RESET_GAME":
            return {
                ...state,
                game: {
                    ...initialState.game,
                    gameState: state.game.gameState, // preserve current gamestate
                    player: { ...initialState.game.player, rating: state.game.player.rating }, // preserve ELO across game resets
                }
            };

        case "SET_IS_AUTH_LOADING":
            return {
                ...state,
                isAuthLoading: action.payload,
            }

        default:
            return state;
    }
}